import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  GlobalUserListItem,
  GlobalUsersFilters,
  GlobalUsersPagination,
  GlobalUsersSummary,
} from '../../types/globalUsers';
import { fetchGlobalUsers } from './actions';

interface GlobalUsersState {
  list: GlobalUserListItem[];
  pagination: GlobalUsersPagination;
  summary: GlobalUsersSummary;
  filters: GlobalUsersFilters;
  isLoading: boolean;
  error: string | null;
}

const initialState: GlobalUsersState = {
  list: [],
  pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 },
  summary: { total: 0, active: 0, admins: 0 },
  filters: { search: '', tenantId: 'all', role: 'all', status: 'all' },
  isLoading: false,
  error: null,
};

const globalUsersSlice = createSlice({
  name: 'globalUsers',
  initialState,
  reducers: {
    setFilter(state, action: PayloadAction<{ key: keyof GlobalUsersFilters; value: string }>) {
      state.filters[action.payload.key] = action.payload.value;
      state.pagination.page = 1;
    },
    resetFilters(state) {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGlobalUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGlobalUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload.data;
        state.pagination = action.payload.pagination;
        state.summary = action.payload.summary;
      })
      .addCase(fetchGlobalUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch users';
      });
  },
});

export const { setFilter, resetFilters, setPage } = globalUsersSlice.actions;
export const globalUsersReducer = globalUsersSlice.reducer;
